import React, { useState } from "react";
import { toast } from "react-toastify";
import { requestAuth } from "../../../components/services/RequestService";
import { Request } from "../../../hooks/Request";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  pdf,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";
import {
  ReportPdfHeader,
  ReportPdfFooter,
  formatGeneratedAt,
} from "./reportBranding";
import {
  addReportBranding,
  styleHeaderRow,
  applyZebraFill,
  finalizeReportSheet,
  addExcelClosingNote,
  EXCEL_COLORS,
} from "./reportExcelBranding";

const styles = StyleSheet.create({
  page: {
    padding: 20,
    paddingBottom: 45,
    fontSize: 7,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    flexDirection: "column",
  },
  table: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 3,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
    minHeight: 20,
    alignItems: "stretch",
  },
  tableRowAlt: { backgroundColor: "#eff6ff" },
  tableRowTotal: { backgroundColor: "#dbeafe" },
  tdTextBold: { fontSize: 5.5, color: "#1e3a8a", fontWeight: "bold" },
  tdTextBoldRight: {
    fontSize: 5.5,
    color: "#1e3a8a",
    fontWeight: "bold",
    textAlign: "right",
  },
  tableHeaderGroup: {
    flexDirection: "row",
    backgroundColor: "#1e3a8a",
    minHeight: 16,
    alignItems: "center",
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
    borderBottomWidth: 1,
    borderBottomColor: "#ffffff",
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#2563eb",
    minHeight: 24,
    alignItems: "center",
  },
  thGroup: {
    paddingHorizontal: 4,
    paddingVertical: 4,
    justifyContent: "center",
  },
  thGroupText: {
    fontSize: 6,
    fontWeight: "bold",
    color: "#ffffff",
    textAlign: "center",
    textTransform: "uppercase",
  },
  th: { paddingHorizontal: 3, paddingVertical: 4, justifyContent: "center" },
  thText: {
    fontSize: 5,
    fontWeight: "bold",
    color: "#ffffff",
    textAlign: "center",
    textTransform: "uppercase",
  },
  td: { paddingHorizontal: 3, paddingVertical: 3, justifyContent: "center" },
  tdText: { fontSize: 5.5, color: "#334155" },
  tdTextRight: { fontSize: 5.5, color: "#334155", textAlign: "right" },
  dividerExt: { borderRightWidth: 1, borderRightColor: "#ffffff" },
  dividerData: { borderRightWidth: 1, borderRightColor: "#94a3b8" },
});

// Anchos de columnas
// DATOS GENERALES (20%): N°IMP 5, PROV 7, PROD 8
// PAGOS EXTERIOR (42%): COND 9, MONTO 7, FECHA 6, ANTICIPO 7, POR PAGAR 7, ESTADO 6
// PAGOS LOCALES (38%): SERVICIO 6, PROV 6, MONTO 6, FECHA 5, ANTICIPO 6, POR PAGAR 5, ESTADO 4
const COL = {
  nroImp: "5%",
  proveedor: "7%",
  producto: "8%",
  condicion: "9%",
  montoUSD: "7%",
  fechaExt: "6%",
  anticipoUSD: "7%",
  porPagarUSD: "7%",
  estadoExt: "6%",
  servicio: "6%",
  provLoc: "6%",
  montoBS: "6%",
  fechaLoc: "5%",
  anticipoBS: "6%",
  porPagarBS: "5%",
  estadoLoc: "4%",
};

const formatDate = (val) => {
  if (!val) return "-";
  try {
    const s = String(val).split("T")[0];
    const [y, m, d] = s.split("-");
    if (y && m && d) return `${d}/${m}/${y}`;
    return val;
  } catch {
    return val;
  }
};

const fmtNum = (val) => {
  const n = Number(val);
  return isNaN(n) || val === "" || val === "-" ? val || "-" : n.toFixed(2);
};

const extractCurrency = (detail, item) => {
  let moneda =
    detail.moneda ||
    detail.Moneda ||
    detail.currency ||
    detail.Currency ||
    detail.tipoMoneda ||
    detail.TipoMoneda ||
    item.moneda ||
    item.Moneda ||
    item.currency ||
    item.Currency ||
    item.tipoMoneda ||
    item.TipoMoneda ||
    "";
  if (
    !moneda &&
    Array.isArray(detail.expenseDetails) &&
    detail.expenseDetails.length > 0
  )
    moneda =
      detail.expenseDetails[0].currency ||
      detail.expenseDetails[0].moneda ||
      "";
  if (
    !moneda &&
    Array.isArray(item.expenseDetails) &&
    item.expenseDetails.length > 0
  )
    moneda =
      item.expenseDetails[0].currency || item.expenseDetails[0].moneda || "";
  if (!moneda) {
    if (detail.amountBs !== undefined || item.amountBs !== undefined)
      moneda = "Bs.";
    else if (detail.amountUsd !== undefined || item.amountUsd !== undefined)
      moneda = "USD";
    else if (detail.amountEur !== undefined || item.amountEur !== undefined)
      moneda = "EUR";
  }
  if (!moneda) {
    const s = String(
      detail.monto || detail.amount || item.monto || item.amount || "",
    ).trim();
    if (/bs\.?$/i.test(s)) moneda = "Bs.";
    else if (/usd$/i.test(s) || /\$us/i.test(s)) moneda = "USD";
    else if (/eur$/i.test(s)) moneda = "EUR";
  }
  if (typeof moneda === "object" && moneda !== null)
    moneda = moneda.name || moneda.label || moneda.value || "";
  if (typeof moneda === "string") {
    const l = moneda.toLowerCase().trim();
    if (l === "bs" || l === "bs." || l === "bolivianos") return "Bs.";
    if (l === "usd" || l === "$us" || l === "dolares" || l === "us$")
      return "USD";
    if (l === "eur" || l === "euros" || l === "eu" || l === "€") return "EUR";
  }
  return moneda;
};

const ReportPaymentsPDF = ({ data }) => {
  const today = formatGeneratedAt();
  const totalGroups = new Set(
    data.map((row) => row._mergeInfo?.groupIdx ?? row),
  ).size;

  return (
    <Document>
      <Page size="LEGAL" orientation="landscape" style={styles.page}>
        <ReportPdfHeader
          title="Reporte de Pagos"
          totalRecords={totalGroups}
          generatedAt={today}
        />

        <View style={styles.table}>
          {/* Fila 1: grupos */}
          <View style={styles.tableHeaderGroup} fixed>
            <View style={[styles.thGroup, styles.dividerExt, { width: "20%" }]}>
              <Text style={styles.thGroupText}>DATOS GENERALES</Text>
            </View>
            <View style={[styles.thGroup, styles.dividerExt, { width: "42%" }]}>
              <Text style={styles.thGroupText}>PAGOS AL EXTERIOR</Text>
            </View>
            <View style={[styles.thGroup, { width: "38%" }]}>
              <Text style={styles.thGroupText}>PAGOS LOCALES</Text>
            </View>
          </View>

          {/* Fila 2: columnas */}
          <View style={styles.tableHeaderRow} fixed>
            {/* DATOS GENERALES */}
            <View style={[styles.th, { width: COL.nroImp }]}>
              <Text style={styles.thText}>N° IMPORTACION</Text>
            </View>
            <View style={[styles.th, { width: COL.proveedor }]}>
              <Text style={styles.thText}>PROVEEDOR</Text>
            </View>
            <View
              style={[styles.th, styles.dividerExt, { width: COL.producto }]}
            >
              <Text style={styles.thText}>PRODUCTO</Text>
            </View>
            {/* PAGOS AL EXTERIOR */}
            <View style={[styles.th, { width: COL.condicion }]}>
              <Text style={styles.thText}>CONDICION DE PAGO</Text>
            </View>
            <View style={[styles.th, { width: COL.montoUSD }]}>
              <Text style={[styles.thText, { textAlign: "right" }]}>
                MONTO TOTAL (USD)
              </Text>
            </View>
            <View style={[styles.th, { width: COL.fechaExt }]}>
              <Text style={styles.thText}>FECHA DE PAGO</Text>
            </View>
            <View style={[styles.th, { width: COL.anticipoUSD }]}>
              <Text style={[styles.thText, { textAlign: "right" }]}>
                ANTICIPO (USD)
              </Text>
            </View>
            <View style={[styles.th, { width: COL.porPagarUSD }]}>
              <Text style={[styles.thText, { textAlign: "right" }]}>
                POR PAGAR (USD)
              </Text>
            </View>
            <View
              style={[styles.th, styles.dividerExt, { width: COL.estadoExt }]}
            >
              <Text style={styles.thText}>ESTADO</Text>
            </View>
            {/* PAGOS LOCALES */}
            <View style={[styles.th, { width: COL.servicio }]}>
              <Text style={styles.thText}>SERVICIO</Text>
            </View>
            <View style={[styles.th, { width: COL.provLoc }]}>
              <Text style={styles.thText}>PROVEEDOR</Text>
            </View>
            <View style={[styles.th, { width: COL.montoBS }]}>
              <Text style={[styles.thText, { textAlign: "right" }]}>
                MONTO TOTAL (BS)
              </Text>
            </View>
            <View style={[styles.th, { width: COL.fechaLoc }]}>
              <Text style={styles.thText}>FECHA DE PAGO</Text>
            </View>
            <View style={[styles.th, { width: COL.anticipoBS }]}>
              <Text style={[styles.thText, { textAlign: "right" }]}>
                ANTICIPO (BS)
              </Text>
            </View>
            <View style={[styles.th, { width: COL.porPagarBS }]}>
              <Text style={[styles.thText, { textAlign: "right" }]}>
                POR PAGAR (BS)
              </Text>
            </View>
            <View style={[styles.th, { width: COL.estadoLoc }]}>
              <Text style={styles.thText}>ESTADO</Text>
            </View>
          </View>

          {/* Filas de datos */}
          {data.map((row, i) => {
            const isAlt =
              (row._mergeInfo ? row._mergeInfo.groupIdx : i) % 2 !== 0;
            const isTotal = !!row._mergeInfo?.isTotal;
            const noBorder = row._mergeInfo && !row._mergeInfo.isLast;
            const isCont = row._mergeInfo && !row._mergeInfo.isFirst;
            const topBorder = isCont
              ? { borderTopWidth: 1, borderTopColor: "#e2e8f0" }
              : {};
            const txt = isTotal ? styles.tdTextBold : styles.tdText;
            const txtRight = isTotal
              ? styles.tdTextBoldRight
              : styles.tdTextRight;
            return (
              <View
                style={[
                  styles.tableRow,
                  isAlt ? styles.tableRowAlt : {},
                  isTotal ? styles.tableRowTotal : {},
                  noBorder ? { borderBottomWidth: 0 } : {},
                ]}
                key={i}
                wrap={false}
              >
                {/* DATOS GENERALES */}
                <View style={[styles.td, { width: COL.nroImp }]}>
                  <Text style={styles.tdText}>
                    {!isCont ? row.nroImportacion : ""}
                  </Text>
                </View>
                <View style={[styles.td, { width: COL.proveedor }]}>
                  <Text style={styles.tdText}>
                    {!isCont ? row.proveedor : ""}
                  </Text>
                </View>
                <View
                  style={[
                    styles.td,
                    styles.dividerData,
                    { width: COL.producto },
                  ]}
                >
                  <Text style={styles.tdText}>
                    {!isCont ? row.producto : ""}
                  </Text>
                </View>
                {/* PAGOS AL EXTERIOR */}
                <View style={[styles.td, topBorder, { width: COL.condicion }]}>
                  <Text style={txt}>{row.condicionPago}</Text>
                </View>
                <View style={[styles.td, topBorder, { width: COL.montoUSD }]}>
                  <Text style={txtRight}>{fmtNum(row.montoTotalUSD)}</Text>
                </View>
                <View style={[styles.td, topBorder, { width: COL.fechaExt }]}>
                  <Text style={txt}>{row.fechaPagoExt}</Text>
                </View>
                <View
                  style={[styles.td, topBorder, { width: COL.anticipoUSD }]}
                >
                  <Text style={txtRight}>{fmtNum(row.anticipoUSD)}</Text>
                </View>
                <View
                  style={[styles.td, topBorder, { width: COL.porPagarUSD }]}
                >
                  <Text style={txtRight}>{fmtNum(row.porPagarUSD)}</Text>
                </View>
                <View
                  style={[
                    styles.td,
                    styles.dividerData,
                    topBorder,
                    { width: COL.estadoExt },
                  ]}
                >
                  <Text style={txt}>{row.estadoExt}</Text>
                </View>
                {/* PAGOS LOCALES */}
                <View style={[styles.td, topBorder, { width: COL.servicio }]}>
                  <Text style={txt}>{row.servicio}</Text>
                </View>
                <View style={[styles.td, topBorder, { width: COL.provLoc }]}>
                  <Text style={txt}>{row.proveedorLocal}</Text>
                </View>
                <View style={[styles.td, topBorder, { width: COL.montoBS }]}>
                  <Text style={txtRight}>{fmtNum(row.montoTotalBS)}</Text>
                </View>
                <View style={[styles.td, topBorder, { width: COL.fechaLoc }]}>
                  <Text style={txt}>{row.fechaPagoLoc}</Text>
                </View>
                <View style={[styles.td, topBorder, { width: COL.anticipoBS }]}>
                  <Text style={txtRight}>{fmtNum(row.anticipoBS)}</Text>
                </View>
                <View style={[styles.td, topBorder, { width: COL.porPagarBS }]}>
                  <Text style={txtRight}>{fmtNum(row.porPagarBS)}</Text>
                </View>
                <View style={[styles.td, topBorder, { width: COL.estadoLoc }]}>
                  <Text style={txt}>{row.estadoLoc}</Text>
                </View>
              </View>
            );
          })}
        </View>

        <ReportPdfFooter />
      </Page>
    </Document>
  );
};

export const ReportPaymentsModal = ({ onClose }) => {
  const [filters, setFilters] = useState({
    nroOperacion: "",
    tipoImportacion: "",
    proveedor: "",
    pagoExterior: "",
    pagoLocal: "",
    estadoPago: "",
    fechaDesde: "",
    fechaHasta: "",
  });
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const { data: typesImport } = Request({
    urlApi: "/Dropdown/types-import/options?PageNumber=1",
  });
  const { data: providers } = Request({
    urlApi: "/Dropdown/providers/options?PageNumber=1",
  });

  const parseArr = (raw) => {
    if (!raw) return [];
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch {
        return [];
      }
    }
    return Array.isArray(raw) ? raw : [];
  };

  const inDateRange = (rawDate, desde, hasta) => {
    if (!desde && !hasta) return true;
    if (!rawDate) return false;
    const d = String(rawDate).split("T")[0];
    if (desde && d < desde) return false;
    if (hasta && d > hasta) return false;
    return true;
  };

  const extractPayment = (item) => {
    let d = {};
    try {
      d = item.description2 ? JSON.parse(item.description2) : {};
    } catch {}
    const label =
      d.concept || d.concepto || d.concept || item.label || item.name || "-";
    const monto = parseFloat(
      d.monto || d.amount || d.total || item.numberValue || item.monto || 0,
    );
    const status = (() => {
      let s =
        d.status || d.estado || d.state || item.status || item.estado || "-";
      if (typeof s === "object" && s !== null) s = s.name || s.label || "";
      return s;
    })();
    const fecha = d.paymentDate || d.tentativeDate || "";
    const anticipo = d.anticipo || d.advance || "";
    const porPagar = d.porPagar || d.balance || "";
    const proveedor = (() => {
      let p =
        d.provider || d.proveedor || item.provider || item.proveedor || "";
      if (typeof p === "object" && p !== null) p = p.name || p.label || "";
      return p;
    })();
    return {
      label,
      monto: isNaN(monto) ? "-" : monto,
      status,
      fecha,
      anticipo,
      porPagar,
      proveedor,
      moneda: extractCurrency(d, item),
    };
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const url = `/Procedure/pag?Fieldnames=codigoDeInterno&Fieldnames=proveedor&Fieldnames=producto&Fieldnames=descripciónGeneralDeLaCompra&Fieldnames=listaDePagos&Fieldnames=registroDePagosLocal&Fieldnames=tipoDeImportacion&IsPaginado=false`;
      const response = await requestAuth("get", url);
      let data = response.data?.data || response.data || [];

      if (filters.nroOperacion)
        data = data.filter((i) =>
          i.codigoDeInterno
            ?.toLowerCase()
            .includes(filters.nroOperacion.toLowerCase()),
        );
      if (filters.tipoImportacion)
        data = data.filter(
          (i) => i.tipoDeImportacion === filters.tipoImportacion,
        );
      if (filters.proveedor) {
        const normalize = (v) =>
          String(v || "")
            .trim()
            .toLowerCase();
        const target = normalize(filters.proveedor);
        data = data.filter((i) => {
          if (normalize(i.proveedor) === target) return true;
          const extArr = parseArr(i.listaDePagos);
          const locArr = parseArr(i.registroDePagosLocal);
          return [...extArr, ...locArr].some(
            (el) => normalize(extractPayment(el).proveedor) === target,
          );
        });
      }

      data = data.map((item) => {
        let extArray = parseArr(item.listaDePagos);
        let locArray = parseArr(item.registroDePagosLocal);

        const matchConcept = (arr, keyword) =>
          arr.filter((el) => {
            const p = extractPayment(el);
            return (p.label || "")
              .toLowerCase()
              .includes(keyword.toLowerCase());
          });
        const matchStatus = (arr, st) =>
          arr.filter((el) => {
            const p = extractPayment(el);
            return p.status.toLowerCase() === st.toLowerCase();
          });
        const matchDateRange = (arr, desde, hasta) =>
          arr.filter((el) => {
            const p = extractPayment(el);
            return inDateRange(p.fecha, desde, hasta);
          });

        if (filters.pagoExterior)
          extArray = matchConcept(extArray, filters.pagoExterior);
        if (filters.pagoLocal)
          locArray = matchConcept(locArray, filters.pagoLocal);
        if (filters.estadoPago) {
          extArray = matchStatus(extArray, filters.estadoPago);
          locArray = matchStatus(locArray, filters.estadoPago);
        }
        if (filters.fechaDesde || filters.fechaHasta) {
          extArray = matchDateRange(
            extArray,
            filters.fechaDesde,
            filters.fechaHasta,
          );
          locArray = matchDateRange(
            locArray,
            filters.fechaDesde,
            filters.fechaHasta,
          );
        }

        return {
          ...item,
          listaDePagos: extArray,
          registroDePagosLocal: locArray,
        };
      });

      if (
        filters.pagoExterior ||
        filters.pagoLocal ||
        filters.estadoPago ||
        filters.fechaDesde ||
        filters.fechaHasta
      ) {
        data = data.filter((item) => {
          if (filters.pagoExterior && item.listaDePagos.length === 0)
            return false;
          if (filters.pagoLocal && item.registroDePagosLocal.length === 0)
            return false;
          if (
            filters.estadoPago &&
            item.listaDePagos.length === 0 &&
            item.registroDePagosLocal.length === 0
          )
            return false;
          if (
            (filters.fechaDesde || filters.fechaHasta) &&
            item.listaDePagos.length === 0 &&
            item.registroDePagosLocal.length === 0
          )
            return false;
          return true;
        });
      }

      setReportData(data);
      toast.success("Reporte generado exitosamente");
    } catch (error) {
      console.error(error);
      toast.error("Error al generar el reporte");
    } finally {
      setLoading(false);
    }
  };

  const getFlattenedData = () => {
    const rows = [];
    let groupIdx = 0;

    reportData.forEach((item) => {
      const extArray = parseArr(item.listaDePagos);
      const locArray = parseArr(item.registroDePagosLocal);
      const maxLen = Math.max(extArray.length, locArray.length, 1);
      const producto =
        item.producto || item.descripciónGeneralDeLaCompra || "-";
      const hasPayments = extArray.length > 0 || locArray.length > 0;
      const totalRows = maxLen + (hasPayments ? 1 : 0);

      const sumMonto = (arr) =>
        arr.reduce((sum, el) => {
          const n = Number(extractPayment(el).monto);
          return sum + (isNaN(n) ? 0 : n);
        }, 0);
      const totalUSD = sumMonto(extArray);
      const totalBS = sumMonto(locArray);

      for (let i = 0; i < totalRows; i++) {
        if (i >= maxLen) {
          rows.push({
            nroImportacion: "",
            proveedor: "",
            producto: "",
            condicionPago: "TOTAL",
            montoTotalUSD: totalUSD,
            fechaPagoExt: "",
            anticipoUSD: "",
            porPagarUSD: "",
            estadoExt: "",
            servicio: "TOTAL",
            proveedorLocal: "",
            montoTotalBS: totalBS,
            fechaPagoLoc: "",
            anticipoBS: "",
            porPagarBS: "",
            estadoLoc: "",
            _mergeInfo: {
              rowspan: 0,
              isFirst: false,
              isLast: true,
              groupIdx,
              isTotal: true,
            },
          });
          continue;
        }
        const ext = extArray[i];
        const loc = locArray[i];

        // Exterior
        let condicionPago = "-",
          montoTotalUSD = "-",
          fechaPagoExt = "-",
          anticipoUSD = "-",
          porPagarUSD = "-",
          estadoExt = "-";
        if (ext) {
          const p = extractPayment(ext);
          condicionPago = p.label;
          montoTotalUSD = p.monto;
          fechaPagoExt = formatDate(p.fecha);
          anticipoUSD = p.anticipo || "-";
          porPagarUSD = p.porPagar || "-";
          estadoExt = p.status;
        } else if (extArray.length > 0) {
          condicionPago = "";
          montoTotalUSD = "";
          fechaPagoExt = "";
          anticipoUSD = "";
          porPagarUSD = "";
          estadoExt = "";
        }

        // Local
        let servicio = "-",
          proveedorLocal = "-",
          montoTotalBS = "-",
          fechaPagoLoc = "-",
          anticipoBS = "-",
          porPagarBS = "-",
          estadoLoc = "-";
        if (loc) {
          const p = extractPayment(loc);
          servicio = p.label;
          proveedorLocal = p.proveedor || "-";
          montoTotalBS = p.monto;
          fechaPagoLoc = formatDate(p.fecha);
          anticipoBS = p.anticipo || "-";
          porPagarBS = p.porPagar || "-";
          estadoLoc = p.status;
        } else if (locArray.length > 0) {
          servicio = "";
          proveedorLocal = "";
          montoTotalBS = "";
          fechaPagoLoc = "";
          anticipoBS = "";
          porPagarBS = "";
          estadoLoc = "";
        }

        rows.push({
          nroImportacion: i === 0 ? item.codigoDeInterno || "-" : "",
          proveedor: i === 0 ? item.proveedor || "-" : "",
          producto: i === 0 ? producto : "",
          condicionPago,
          montoTotalUSD,
          fechaPagoExt,
          anticipoUSD,
          porPagarUSD,
          estadoExt,
          servicio,
          proveedorLocal,
          montoTotalBS,
          fechaPagoLoc,
          anticipoBS,
          porPagarBS,
          estadoLoc,
          _mergeInfo: {
            rowspan: i === 0 ? totalRows : 0,
            isFirst: i === 0,
            isLast: i === totalRows - 1,
            groupIdx,
          },
        });
      }
      groupIdx++;
    });
    return rows;
  };

  const handleDownloadExcel = async () => {
    if (!reportData || reportData.length === 0) {
      toast.warning("No hay datos para exportar");
      return;
    }
    const data = getFlattenedData();
    const generatedAt = formatGeneratedAt();
    const totalGroups = new Set(
      data.map((row) => row._mergeInfo?.groupIdx ?? row),
    ).size;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("REPORTE_DE_PAGOS");
    const COL_COUNT = 16;

    worksheet.columns = [
      { key: "nroImportacion", width: 18 },
      { key: "proveedor", width: 22 },
      { key: "producto", width: 22 },
      { key: "condicionPago", width: 20 },
      { key: "montoTotalUSD", width: 16 },
      { key: "fechaPagoExt", width: 14 },
      { key: "anticipoUSD", width: 14 },
      { key: "porPagarUSD", width: 14 },
      { key: "estadoExt", width: 14 },
      { key: "servicio", width: 20 },
      { key: "proveedorLocal", width: 20 },
      { key: "montoTotalBS", width: 16 },
      { key: "fechaPagoLoc", width: 14 },
      { key: "anticipoBS", width: 14 },
      { key: "porPagarBS", width: 14 },
      { key: "estadoLoc", width: 14 },
    ];

    const groupRow = await addReportBranding(workbook, worksheet, {
      title: "Reporte de Pagos",
      colCount: COL_COUNT,
      totalRecords: totalGroups,
      generatedAt,
    });

    // Fila de grupos (DATOS GENERALES / PAGOS AL EXTERIOR / PAGOS LOCALES)
    worksheet.mergeCells(`A${groupRow}:C${groupRow}`);
    worksheet.getCell(`A${groupRow}`).value = "DATOS GENERALES";
    worksheet.mergeCells(`D${groupRow}:I${groupRow}`);
    worksheet.getCell(`D${groupRow}`).value = "PAGOS AL EXTERIOR";
    worksheet.mergeCells(`J${groupRow}:P${groupRow}`);
    worksheet.getCell(`J${groupRow}`).value = "PAGOS LOCALES";
    worksheet.getRow(groupRow).eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: EXCEL_COLORS.primary },
      };
      cell.font = { color: { argb: "FFFFFFFF" }, bold: true, size: 10 };
      cell.alignment = { vertical: "middle", horizontal: "center" };
    });
    worksheet.getRow(groupRow).height = 20;

    // Fila de columnas
    const headerRow = groupRow + 1;
    const headers = [
      "N° IMPORTACION",
      "PROVEEDOR",
      "PRODUCTO",
      "CONDICION DE PAGO",
      "MONTO TOTAL (USD)",
      "FECHA DE PAGO",
      "ANTICIPO (USD)",
      "POR PAGAR (USD)",
      "ESTADO",
      "SERVICIO",
      "PROVEEDOR",
      "MONTO TOTAL (BS)",
      "FECHA DE PAGO",
      "ANTICIPO (BS)",
      "POR PAGAR (BS)",
      "ESTADO",
    ];
    worksheet.getRow(headerRow).values = headers;
    styleHeaderRow(worksheet.getRow(headerRow), EXCEL_COLORS.accent, 28);

    let rowIndex = headerRow + 1;
    data.forEach((row) => {
      const isAlt =
        (row._mergeInfo ? row._mergeInfo.groupIdx : rowIndex) % 2 !== 0;
      const excelRow = worksheet.addRow({
        nroImportacion: row.nroImportacion,
        proveedor: row.proveedor,
        producto: row.producto,
        condicionPago: row.condicionPago,
        montoTotalUSD: isNaN(Number(row.montoTotalUSD))
          ? row.montoTotalUSD
          : Number(row.montoTotalUSD),
        fechaPagoExt: row.fechaPagoExt,
        anticipoUSD: isNaN(Number(row.anticipoUSD))
          ? row.anticipoUSD
          : Number(row.anticipoUSD),
        porPagarUSD: isNaN(Number(row.porPagarUSD))
          ? row.porPagarUSD
          : Number(row.porPagarUSD),
        estadoExt: row.estadoExt,
        servicio: row.servicio,
        proveedorLocal: row.proveedorLocal,
        montoTotalBS: isNaN(Number(row.montoTotalBS))
          ? row.montoTotalBS
          : Number(row.montoTotalBS),
        fechaPagoLoc: row.fechaPagoLoc,
        anticipoBS: isNaN(Number(row.anticipoBS))
          ? row.anticipoBS
          : Number(row.anticipoBS),
        porPagarBS: isNaN(Number(row.porPagarBS))
          ? row.porPagarBS
          : Number(row.porPagarBS),
        estadoLoc: row.estadoLoc,
      });
      applyZebraFill(excelRow, isAlt);

      if (row._mergeInfo?.isTotal) {
        excelRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: EXCEL_COLORS.primary } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: EXCEL_COLORS.muted },
          };
        });
      }

      if (row._mergeInfo?.isFirst && row._mergeInfo.rowspan > 1) {
        const span = row._mergeInfo.rowspan;
        ["A", "B", "C"].forEach((col) => {
          worksheet.mergeCells(
            `${col}${rowIndex}:${col}${rowIndex + span - 1}`,
          );
          worksheet.getCell(`${col}${rowIndex}`).alignment = {
            vertical: "middle",
            horizontal: "left",
            wrapText: true,
          };
        });
      }
      rowIndex++;
    });

    ["E", "H", "I", "L", "O", "P"].forEach((col) => {
      worksheet.getColumn(col).numFmt = "#,##0.00";
    });

    const lastDataRow = rowIndex - 1;
    finalizeReportSheet(worksheet, {
      headerTopRow: groupRow,
      headerBottomRow: headerRow,
      lastDataRow,
      colCount: COL_COUNT,
    });
    addExcelClosingNote(worksheet, {
      row: lastDataRow + 2,
      colCount: COL_COUNT,
      generatedAt,
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "REPORTE_DE_PAGOS.xlsx");
    toast.success("Excel descargado exitosamente");
  };

  const handleDownloadPDF = async () => {
    if (!reportData || reportData.length === 0) {
      toast.warning("No hay datos para exportar");
      return;
    }
    toast.info("Generando PDF, por favor espere...");
    try {
      const data = getFlattenedData();
      const doc = <ReportPaymentsPDF data={data} />;
      const asPdf = pdf([]);
      asPdf.updateContainer(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "REPORTE_DE_PAGOS.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF descargado exitosamente");
    } catch (error) {
      console.error(error);
      toast.error("Error al generar PDF");
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-2xl w-[98vw] max-w-[1600px] max-h-[95vh] flex flex-col relative overflow-hidden uppercase">
      {/* Cabecera */}
      <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white flex justify-between items-center shrink-0">
        <h2 className="text-lg md:text-xl font-bold text-gray-800 m-0">
          REPORTE DE PAGOS
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-red-500 transition-colors"
        >
          <i className="fas fa-times text-xl"></i>
        </button>
      </div>

      {/* Filtros */}
      <div className="p-4 md:p-6 overflow-y-auto flex-1 bg-white">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              N° IMPORTACION
            </label>
            <input
              type="text"
              placeholder="Ej: 26-PCIME..."
              value={filters.nroOperacion}
              onChange={(e) =>
                setFilters({ ...filters, nroOperacion: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Importación
            </label>
            <select
              value={filters.tipoImportacion}
              onChange={(e) =>
                setFilters({ ...filters, tipoImportacion: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 bg-white"
            >
              <option value="">Todos</option>
              {typesImport?.map((t, i) => (
                <option key={i} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proveedor
            </label>
            <select
              value={filters.proveedor}
              onChange={(e) =>
                setFilters({ ...filters, proveedor: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 bg-white"
            >
              <option value="">Todos</option>
              {providers?.map((p, i) => (
                <option key={i} value={p.name}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado de Pago
            </label>
            <select
              value={filters.estadoPago}
              onChange={(e) =>
                setFilters({ ...filters, estadoPago: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 bg-white"
            >
              <option value="">Todos</option>
              <option value="Planificado">Planificado</option>
              <option value="Solicitado">Solicitado</option>
              <option value="Pagado">Pagado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pago al Exterior
            </label>
            <input
              type="text"
              placeholder="Ej: Anticipo..."
              value={filters.pagoExterior}
              onChange={(e) =>
                setFilters({ ...filters, pagoExterior: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pago Local
            </label>
            <input
              type="text"
              placeholder="Ej: Seguro, Naviera..."
              value={filters.pagoLocal}
              onChange={(e) =>
                setFilters({ ...filters, pagoLocal: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha de Pago (Desde)
            </label>
            <input
              type="date"
              value={filters.fechaDesde}
              onChange={(e) =>
                setFilters({ ...filters, fechaDesde: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha de Pago (Hasta)
            </label>
            <input
              type="date"
              value={filters.fechaHasta}
              onChange={(e) =>
                setFilters({ ...filters, fechaHasta: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500"
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-3 mb-6 border-b border-gray-200 pb-4">
          <button
            className={`px-4 py-2 text-white rounded-md transition-colors flex items-center gap-2 ${!reportData ? "bg-green-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}
            onClick={handleDownloadExcel}
            disabled={!reportData || loading}
          >
            <i className="fas fa-file-excel"></i>
            <span>Descargar Excel</span>
          </button>
          <button
            className={`px-4 py-2 text-white rounded-md transition-colors flex items-center gap-2 ${!reportData ? "bg-red-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
            onClick={handleDownloadPDF}
            disabled={!reportData || loading}
          >
            <i className="fas fa-file-pdf"></i>
            <span>Descargar PDF</span>
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            onClick={handleGenerate}
            disabled={loading}
          >
            <i
              className={
                loading ? "fas fa-spinner fa-spin mr-2" : "fas fa-eye mr-2"
              }
            ></i>
            <span>{loading ? "Cargando..." : "Previsualizar"}</span>
          </button>
        </div>

        {/* Tabla previsualización */}
        {reportData && (
          <div className="overflow-x-auto border border-gray-200 rounded-md shadow-sm pb-2">
            <table
              className="w-full text-xs text-left border-collapse"
              style={{ minWidth: "1400px" }}
            >
              <thead>
                {/* Fila 1: grupos */}
                <tr className="bg-[#1e3a8a] text-white">
                  <th
                    colSpan={3}
                    className="px-3 py-2 text-center text-xs font-bold border-r border-blue-400 tracking-wide"
                  >
                    DATOS GENERALES
                  </th>
                  <th
                    colSpan={6}
                    className="px-3 py-2 text-center text-xs font-bold border-r border-blue-400 tracking-wide"
                  >
                    PAGOS AL EXTERIOR
                  </th>
                  <th
                    colSpan={7}
                    className="px-3 py-2 text-center text-xs font-bold tracking-wide"
                  >
                    PAGOS LOCALES
                  </th>
                </tr>
                {/* Fila 2: columnas */}
                <tr className="bg-[#2563eb] text-white">
                  <th className="px-2 py-2 whitespace-nowrap text-center">
                    N° IMPORTACION
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center">
                    PROVEEDOR
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center border-r border-blue-300">
                    PRODUCTO
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center">
                    CONDICION DE PAGO
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center">
                    MONTO TOTAL (USD)
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center">
                    FECHA DE PAGO
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center">
                    ANTICIPO (USD)
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center">
                    POR PAGAR (USD)
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center border-r border-blue-300">
                    ESTADO
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center">
                    SERVICIO
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center">
                    PROVEEDOR
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center">
                    MONTO TOTAL (BS)
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center">
                    FECHA DE PAGO
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center">
                    ANTICIPO (BS)
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center">
                    POR PAGAR (BS)
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap text-center">
                    ESTADO
                  </th>
                </tr>
              </thead>
              <tbody>
                {getFlattenedData().length === 0 ? (
                  <tr>
                    <td colSpan={16} className="text-center py-6 text-gray-500">
                      No se encontraron resultados para los filtros
                      seleccionados.
                    </td>
                  </tr>
                ) : (
                  getFlattenedData().map((row, idx) => {
                    const isAlt =
                      (row._mergeInfo ? row._mergeInfo.groupIdx : idx) % 2 !==
                      0;
                    const isTotal = !!row._mergeInfo?.isTotal;
                    const statusBadge = (st) => {
                      if (!st || st === "-")
                        return <span className="text-gray-400">-</span>;
                      const cls =
                        st.toLowerCase() === "pagado"
                          ? "bg-green-100 text-green-800"
                          : st.toLowerCase() === "solicitado"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-yellow-100 text-yellow-800";
                      return (
                        <span
                          className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${cls}`}
                        >
                          {st}
                        </span>
                      );
                    };
                    const cell = isTotal
                      ? "px-2 py-2 font-bold text-blue-900"
                      : "px-2 py-2 text-gray-700";
                    return (
                      <tr
                        key={idx}
                        className={`border-b border-gray-200 align-top transition-colors hover:bg-blue-50 ${isTotal ? "bg-blue-50" : isAlt ? "bg-slate-50" : ""}`}
                      >
                        <td className="px-2 py-2 font-medium text-gray-800 whitespace-nowrap">
                          {row.nroImportacion}
                        </td>
                        <td className={cell}>{row.proveedor}</td>
                        <td className={`${cell} border-r border-gray-200`}>
                          {row.producto}
                        </td>
                        <td className={cell}>{row.condicionPago}</td>
                        <td
                          className={`${cell.replace("text-gray-700", "")} text-right`}
                        >
                          {fmtNum(row.montoTotalUSD)}
                        </td>
                        <td className={`${cell} whitespace-nowrap`}>
                          {row.fechaPagoExt}
                        </td>
                        <td
                          className={`${cell.replace("text-gray-700", "")} text-right`}
                        >
                          {fmtNum(row.anticipoUSD)}
                        </td>
                        <td
                          className={`${cell.replace("text-gray-700", "")} text-right`}
                        >
                          {fmtNum(row.porPagarUSD)}
                        </td>
                        <td className="px-2 py-2 text-center border-r border-gray-200">
                          {statusBadge(row.estadoExt)}
                        </td>
                        <td className={cell}>{row.servicio}</td>
                        <td className={cell}>{row.proveedorLocal}</td>
                        <td
                          className={`${cell.replace("text-gray-700", "")} text-right`}
                        >
                          {fmtNum(row.montoTotalBS)}
                        </td>
                        <td className={`${cell} whitespace-nowrap`}>
                          {row.fechaPagoLoc}
                        </td>
                        <td
                          className={`${cell.replace("text-gray-700", "")} text-right`}
                        >
                          {fmtNum(row.anticipoBS)}
                        </td>
                        <td
                          className={`${cell.replace("text-gray-700", "")} text-right`}
                        >
                          {fmtNum(row.porPagarBS)}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {statusBadge(row.estadoLoc)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pie */}
      <div className="px-4 py-3 md:px-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-2 shrink-0">
        <button
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-medium shadow-sm"
          onClick={onClose}
        >
          Cerrar
        </button>
      </div>
    </div>
  );
};
